// ===========================================
// Database API Functions
// ===========================================

import type { DebtData } from '@/types';
import type { CaseWithFinancials } from '@/types/core-api';
import type { OphelosPaymentPlanResponse } from '@/types/payment-plan';
import type { CustomerPortalAccountData } from '@/types/database-api';
import { getVerificationContactsFromConnect } from '@/lib/connect-api';
import { fromMillicents } from '@/lib/currency';

export async function fetchAccountContacts(): Promise<{ email: string | null; phone: string | null }> {
  try {
    return await getVerificationContactsFromConnect();
  } catch (error) {
    console.error('[fetchAccountContacts] Error fetching contacts:', error);
    return { email: null, phone: null };
  }
}

/**
 * Map case financial data from Core API to DebtData for the UI
 * Takes case data from Core API and account data from database
 *
 * @param cases - Cases with financial data from Core API (TigerBeetle ledger)
 * @param accountData - Account metadata from database
 * @returns Array of DebtData for UI consumption
 */
export function mapCasesToDebts(
  cases: CaseWithFinancials[],
  accountData: CustomerPortalAccountData,
  accountPaymentPlan: OphelosPaymentPlanResponse | null = null
): DebtData[] {
  return cases.map((caseWithFinancials) => {
    const {
      case: caseData,
      outstanding_balance,
      original_balance,
      payment_plan,
      transactions,
      migrated_history,
      credit_applied_millicents,
    } = caseWithFinancials;

    // A non-null payment_plan_id on a case under an active account-scoped plan
    // unambiguously means the case is a member — Decision #6 (#3111) guarantees
    // case-scoped and account-scoped plans don't coexist on the same account.
    // (Backend derivation lives in CustomerPortalService.get_account_view.)
    const isAccountPlanMember = accountPaymentPlan != null && caseData.payment_plan_id != null;

    // Client display name (prefer client_name from clients table, fall back to original_creditor)
    const originalCreditor = caseData.client_name || caseData.original_creditor || 'Unknown Creditor';
    // Current creditor (who now manages the debt collection)
    const creditorName = caseData.creditor_name || 'Unknown Creditor';
    // Client logo URL from clients table
    const creditorLogoUrl = caseData.client_logo_url || undefined;

    // Current and original balances from Core API (TigerBeetle ledger)
    const currentBalance = fromMillicents(outstanding_balance.total_millicents);
    const originalAmount = fromMillicents(original_balance.total_millicents);

    // Determine UI status based on case status, balance, and payment plan.
    // Section (Open debts vs Active payment plans) is determined by Dashboard grouping (journey node + has active plan).
    let status: 'unpaid' | 'in-progress' | 'completed' = 'unpaid';
    if (caseData.journey_status === 'closed' || currentBalance === 0) {
      status = 'completed';
    } else if (
      caseData.journey_status === 'paused' ||
      (caseData.journey_status === 'active' && currentBalance < originalAmount && currentBalance > 0) ||
      payment_plan
    ) {
      status = 'in-progress';
    }

    // Pre-Forest history on a migrated case. Amounts arrive in millicents like every
    // other figure on this response; DebtData carries currency units throughout.
    const migratedHistory = migrated_history
      ? {
          openedOn: migrated_history.opened_on,
          previousOriginalAmount: fromMillicents(migrated_history.previous_original_millicents),
          migratedOn: migrated_history.migrated_on,
          carriedOverAmount: fromMillicents(migrated_history.carried_over_millicents),
          events: migrated_history.events.map((event) => ({
            reference: event.reference,
            type: event.type,
            occurredOn: event.occurred_on,
            amount: fromMillicents(event.amount_millicents),
            method: event.method ?? undefined,
          })),
        }
      : undefined;

    // Original balance breakdown (from TigerBeetle ledger)
    const originalBreakdown = {
      principal: fromMillicents(original_balance.principal_millicents),
      interest: fromMillicents(original_balance.interest_millicents),
      fees: fromMillicents(original_balance.fees_millicents),
      total: fromMillicents(original_balance.total_millicents),
    };

    // Outstanding balance breakdown (from TigerBeetle ledger)
    const outstandingBreakdown = {
      principal: fromMillicents(outstanding_balance.principal_millicents),
      interest: fromMillicents(outstanding_balance.interest_millicents),
      fees: fromMillicents(outstanding_balance.fees_millicents),
      total: fromMillicents(outstanding_balance.total_millicents),
    };

    // Adjustment breakdown (from outstanding balance metadata)
    const adjustmentBreakdown = {
      principal: fromMillicents(outstanding_balance.adjustment_principal_millicents ?? 0),
      interest: fromMillicents(outstanding_balance.adjustment_interest_millicents ?? 0),
      fees: fromMillicents(outstanding_balance.adjustment_fees_millicents ?? 0),
      total: fromMillicents(outstanding_balance.adjustment_total_millicents ?? 0),
    };

    // Payment breakdown (from outstanding balance metadata)
    const paymentBreakdown = {
      principal: fromMillicents(outstanding_balance.payment_principal_millicents ?? 0),
      interest: fromMillicents(outstanding_balance.payment_interest_millicents ?? 0),
      fees: fromMillicents(outstanding_balance.payment_fees_millicents ?? 0),
      total: fromMillicents(outstanding_balance.payment_total_millicents ?? 0),
    };

    return {
      // Identifiers
      id: caseData.id,
      externalCaseRef: caseData.external_case_ref,
      clientId: caseData.client_id ?? null,
      originalCreditor,
      creditorName,
      creditorLogoUrl,
      displayFields: caseData.display_fields ?? [],
      description: `Case ${caseData.external_case_ref || caseData.id.substring(5, 13)}`,
      currency: outstanding_balance.currency || 'EUR',
      // Use masked account ref from database
      accountNumber: accountData.account_external_ref || '**** ****',

      // Case status
      caseStatus: caseData.journey_status,
      journeyStatus: caseData.journey_status,
      journeyNode: caseData.journey_node,
      status,

      // Financial data
      originalAmount,
      currentBalance,
      creditApplied: fromMillicents(credit_applied_millicents ?? 0),
      originalBreakdown,
      discountApplied: fromMillicents(outstanding_balance.discount_total_millicents ?? 0),
      outstandingBreakdown,
      adjustmentBreakdown,
      paymentBreakdown,

      // True when this case is covered by the active account-scoped plan.
      isAccountPlanMember,
      migratedHistory,

      // Related data
      transactions: transactions ?? [],
      // Case-scoped only: an account plan covers many cases, so it belongs to
      // none of them — buildAccountEntry renders it once instead.
      paymentPlan: payment_plan
        ? {
            externalPaymentPlanId: payment_plan.id,
            status: payment_plan.status,
            totalPayments: payment_plan.schedule.length,
            completedPayments: payment_plan.schedule.filter((item) => item.status === 'succeeded').length,
            fullPlan: payment_plan,
          }
        : null,
    };
  });
}
